import React, { useState } from 'react';

/**
 * Fetches a one-time sign-in link for someone who hasn't set a password yet, so
 * the employer can pass it on directly. Supabase reports an invite as sent even
 * when the mail is later dropped or quarantined, so email on its own isn't a
 * dependable way in.
 */
export function SignInLinkButton({ email, onGetLink, label = 'Get sign-in link', size = 'xs' }) {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  async function fetchLink() {
    setLoading(true);
    setErr('');
    setCopied(false);
    try {
      const l = await onGetLink(email);
      if (!l) throw new Error('No link came back.');
      setLink(l);
    } catch (e) {
      setErr(e.message || 'Could not create a link.');
    }
    setLoading(false);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setErr('Copy failed — select the link and copy it manually.');
    }
  }

  if (!link) {
    return (
      <>
        <button className={`btn ${size} outline`} disabled={loading} onClick={fetchLink}>
          {loading ? 'Creating…' : label}
        </button>
        {err && <div style={{ fontSize: 10.5, color: 'var(--red)', marginTop: 4 }}>{err}</div>}
      </>
    );
  }

  return (
    <div style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.3)', fontSize: 11.5, marginTop: 6 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        Send {email} this link:
      </div>
      <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 10.5, background: 'var(--bg)', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)', userSelect: 'all' }}>
        {link}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
        <button className="btn xs outline" onClick={copy}>{copied ? '✓ Copied' : 'Copy link'}</button>
        <button className="btn xs outline" onClick={() => setLink('')}>Hide</button>
      </div>
      <div style={{ marginTop: 6, color: 'var(--muted)', lineHeight: 1.5 }}>
        Opens SignedOff signed in as them, and asks them to set a password. It can only
        be used once and expires — generate a fresh one if it goes stale. Any link
        emailed earlier has now stopped working.
      </div>
      {err && <div style={{ fontSize: 10.5, color: 'var(--red)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}
